import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export interface Tag {
  id: string;
  name: string;
  type: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  usageCount: number;
  orderTypes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface GroupedTag {
  id: string;
  name: string;
  orderTypes: string[];
  sortOrder: number;
}

export interface TagCategoryFromApi {
  id: string;
  displayName: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface TagsResponse {
  tags: Tag[];
  grouped: Record<string, GroupedTag[]>;
  categories: TagCategoryFromApi[];
}

export type ExperienceType = "dine-in" | "takeout" | "delivery";

export interface TagItem {
  id: string;
  label: string;
  orderTypes: ExperienceType[];
}

export interface TagCategory {
  id: string;
  label: string;
  experienceTypes: ExperienceType[];
  tags: TagItem[];
}

const TAG_TYPE_LABELS: Record<string, string> = {
  cuisine: "Cuisine",
  ambiance: "Atmosphere",
  service: "Service",
  dietary: "Dietary",
  delivery_speed: "Speed",
  food_quality_arrival: "Food Quality",
  packaging: "Packaging",
  value_price: "Value",
  highlights: "Highlights",
};

const ORDER_TYPE_MAP: Record<string, ExperienceType[]> = {
  cuisine: ["dine-in", "takeout", "delivery"],
  ambiance: ["dine-in"],
  service: ["dine-in"],
  dietary: ["dine-in", "takeout", "delivery"],
  delivery_speed: ["delivery", "takeout"],
  food_quality_arrival: ["takeout", "delivery"],
  packaging: ["takeout", "delivery"],
  value_price: ["dine-in", "takeout", "delivery"],
  highlights: ["dine-in", "takeout", "delivery"],
};

/**
 * Normalize DB/API order type values to UI format.
 * DB stores: dine_in, takeout, delivery (underscores)
 * UI uses:   dine-in, takeout, delivery (hyphen for dine-in)
 */
function normalizeOrderType(raw: string): ExperienceType | null {
  const normalized = raw.trim().toLowerCase();
  if (normalized === "dine_in" || normalized === "dine-in") return "dine-in";
  if (normalized === "takeout") return "takeout";
  if (normalized === "delivery") return "delivery";
  return null;
}

function normalizeOrderTypes(raw: string[]): ExperienceType[] {
  if (!raw || raw.length === 0) return ["dine-in", "takeout", "delivery"];
  const result: ExperienceType[] = [];
  for (const r of raw) {
    const normalized = normalizeOrderType(r);
    if (normalized && !result.includes(normalized)) {
      result.push(normalized);
    }
  }
  return result.length > 0 ? result : ["dine-in", "takeout", "delivery"];
}

export function useTags() {
  const query = useQuery<TagsResponse>({
    queryKey: ["/api/tags"],
    staleTime: 5 * 60 * 1000,
  });

  const tagCategories: TagCategory[] = useMemo(() => {
    if (!query.data?.grouped) return [];

    const categoryMetadata = query.data.categories || [];
    const categoryOrderMap = new Map(
      categoryMetadata.map((c) => [c.id, c.sortOrder])
    );
    const categoryLabelMap = new Map(
      categoryMetadata.map((c) => [c.id, c.displayName])
    );
    const categoryActiveMap = new Map(
      categoryMetadata.map((c) => [c.id, c.isActive])
    );

    const categories = Object.entries(query.data.grouped)
      .filter(([type]) => categoryActiveMap.get(type) !== false)
      .map(([type, groupedTags]) => {
        // Build tag items with normalized orderTypes
        const tagItems: TagItem[] = [...groupedTags]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((t) => ({
            id: t.id,
            label: t.name,
            orderTypes: normalizeOrderTypes(t.orderTypes),
          }));

        // Derive category experienceTypes from ORDER_TYPE_MAP first (explicit intent),
        // then fall back to union of all tag orderTypes in this category.
        const categoryExperienceTypes: ExperienceType[] =
          ORDER_TYPE_MAP[type] ||
          (Array.from(
            new Set(tagItems.flatMap((t) => t.orderTypes))
          ) as ExperienceType[]);

        return {
          id: type,
          label:
            categoryLabelMap.get(type) ||
            TAG_TYPE_LABELS[type] ||
            type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          experienceTypes: categoryExperienceTypes,
          tags: tagItems,
          sortOrder: categoryOrderMap.get(type) ?? 999,
        };
      });

    return categories.sort((a, b) => a.sortOrder - b.sortOrder);
  }, [query.data?.grouped, query.data?.categories]);

  const getTagsByType = useMemo(() => {
    return (type: string): TagItem[] => {
      if (!query.data?.grouped) return [];
      return [...(query.data.grouped[type] || [])]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((t) => ({
          id: t.id,
          label: t.name,
          orderTypes: normalizeOrderTypes(t.orderTypes),
        }));
    };
  }, [query.data?.grouped]);

  const getCuisineTags = useMemo(() => {
    return () => getTagsByType("cuisine");
  }, [getTagsByType]);

  /**
   * Returns categories valid for the given experience type,
   * with each category's tags also filtered to only those valid for that type.
   * Empty categories (no valid tags) are omitted.
   */
  const getTagsForExperienceType = useMemo(() => {
    return (experienceType: ExperienceType): TagCategory[] => {
      return tagCategories
        .filter((cat) => cat.experienceTypes.includes(experienceType))
        .map((cat) => ({
          ...cat,
          tags: cat.tags.filter((t) => t.orderTypes.includes(experienceType)),
        }))
        .filter((cat) => cat.tags.length > 0);
    };
  }, [tagCategories]);

  return {
    ...query,
    tagCategories,
    getTagsByType,
    getCuisineTags,
    getTagsForExperienceType,
    allTags: query.data?.tags || [],
    groupedTags: query.data?.grouped || {},
  };
}
